<template>
  <div @click="() => selectAlbum()">
    <SlideBlur v-if="!slide || !contentIsLoaded" />

    <div
      v-if="slide"
      class="frisbuy-story-slide"
      :class="{ 'frisbuy-blur-background': !slide || !contentIsLoaded }"
      :style="{
        ...highlightImagePositionStyles,
        ...backgroundImageStyles,
      }"
      @click.self="$emit('change-album')"
      @mousedown.stop
    >
      <img
        ref="image"
        :data-src="getDomainSubstitute(imageSource.imageHighRes)"
        @error="errorImageHandler"
      />
      <video
        v-if="storyMedia.videoUrl"
        ref="video"
        playsinline
        preload="metadata"
        :muted="!preview.playWithSound"
        :data-src="getDomainSubstitute(storyMedia.videoUrl)"
        @canplay="contentIsLoaded = true"
        @error="errorVideoHandler"
        @click.self.stop="$emit('change-album')"
        @mousedown.stop
      ></video>
    </div>

    <InteractionWrapper v-if="isSelectedAlbum" ref="InteractionWrapper">
      <div
        v-if="isSelectedInputInteraction"
        class="frisbuy-slide-background-click-blocker"
      ></div>

      <SlideNavigation
        :isDisablePointerEvents="isSelectedInputInteraction"
        navigationType="default"
        @clickPrev="$emit('navigationSwitchToNext', -1)"
        @clickNext="$emit('navigationSwitchToNext', 1)"
        @touchstart="$emit('navigationTouchPause')"
      />

      <EmojisAnimation
        v-if="interactions"
        :isShowEmojiAnimation="isShowEmojiAnimation"
        :emojiIconUrl="clickedEmojiIconUrl"
      />
      <div
        v-for="(interaction, index) in interactions"
        v-show="contentIsLoaded"
        :style="getInteractionPositionStyles(interaction)"
        :key="
          interaction.type +
          interaction.x +
          interaction.y +
          interaction.scale +
          index
        "
        class="frisbuy-interaction-frame"
      >
        <EmojisKit
          v-if="interaction.type === 'emoji'"
          :interaction="interaction"
          @clickEmoji="
            (emoji) =>
              onClickInteractionHandler({
                type: interaction.type,
                index: index,
                ...emoji,
              })
          "
        />
        <PollForm
          v-else-if="interaction.type === 'poll'"
          :interaction="interaction"
          :isQuerySuccess="isPollQuerySuccess"
          @chooseAnswer="
            (answer) =>
              onClickInteractionHandler({
                index: index,
                type: interaction.type,
                answer: answer,
              })
          "
        />
        <QuestionForm
          v-else-if="interaction.type === 'question'"
          :interaction="interaction"
          :isQuerySuccess="isQuestionQuerySuccess"
          @selectInput="() => questionFormSelectInputHandler(true)"
          @unSelectInput="() => questionFormSelectInputHandler(false)"
          @getAnswer="
            (answer) =>
              onClickInteractionHandler({ type: interaction.type, ...answer })
          "
        />
      </div>

      <template v-if="stickers?.pictureStickers">
        <PictureSticker
          v-for="pictureSticker in stickers.pictureStickers"
          v-show="contentIsLoaded"
          :key="`pictureSticker_${getKeyByParams(pictureSticker)}`"
          :sticker="pictureSticker"
          :style="getInteractionPositionStyles(pictureSticker)"
        />
      </template>

      <template v-if="stickers?.textStickers">
        <TextSticker
          v-for="textSticker in stickers.textStickers"
          v-show="contentIsLoaded"
          :key="`textSticker_${getKeyByParams(textSticker)}`"
          :sticker="textSticker"
          :style="{
            ...getInteractionPositionStyles(textSticker),
          }"
        />
      </template>

      <template v-if="callToActions">
        <template
          v-for="(action, index) in callToActions"
          :key="`callToAction_${index}`"
        >
          <CallToAction
            v-show="contentIsLoaded"
            @click.prevent="() => $emit('pauseSlide')"
            @clickLink="
              (link, linkTitle) => sendClickLinkAnalytic(link, linkTitle)
            "
            :callToAction="action"
            :is_nofollow_cta="settings.is_nofollow_cta"
            :style="{
              ...getInteractionPositionStyles(action),
            }"
          ></CallToAction>
        </template>
      </template>
    </InteractionWrapper>
  </div>
</template>

<script>
// Mixins
import analytics from "@/common/mixins/analytics";
// Components
import { SlideBlur } from "@/components/stories";
import { getImagePositionStyles } from "@/scripts/services";
import {
  EmojisKit,
  PollForm,
  QuestionForm,
  InteractionWrapper,
  EmojisAnimation,
  CallToAction,
} from "@/components/interactions";
import { PictureSticker, TextSticker } from "@/components/stickers";
import SlideNavigation from "@/components/stories/SlideNavigation.vue";
// API
import { getAnonimousUserCode, sendInteraction } from "@/api/interactions";
import { recoveryURL } from "@/api/api";
// Services
import { MediaCatcher } from "@/common/services/ErrorHandler/ErrorCatcher";
import { DomainSubstitute } from "@/common/services/DomainSubstitute/DomainSubstitute";
// Consts
import widgetTypes from "@/common/consts/widgetTypes";

export default {
  // TODO: rewrite mixins to pure js methods or use composition api
  // for importing like sendAnalyticEvent from "@/common/mixins/analytics";
  mixins: [analytics],

  inject: ["preview", "settings"],

  props: ["isSelectedAlbum", "playWithSound", "album", "selectedPostIndex"],

  emits: [
    "navigationTouchPause",
    "navigationSwitchToNext",
    "pauseSlide",
    "playSlide",
    "getCountOfActiveQueries",
    "resume-slide",
    "change-album",
  ],

  components: {
    SlideBlur,
    EmojisKit,
    PollForm,
    QuestionForm,
    InteractionWrapper,
    EmojisAnimation,
    PictureSticker,
    TextSticker,
    SlideNavigation,
    CallToAction,
  },

  data() {
    return {
      contentIsLoaded: false,
      previewImage: null,
      loadFailTimeout: null,

      interactions: undefined,
      isShowEmojiAnimation: false,
      clickedEmojiIconUrl: undefined,
      isEmojiQuerySuccess: false,
      isPollQuerySuccess: false,
      isQuestionQuerySuccess: false,
      isSelectedInputInteraction: false,

      countOfActiveQueries: 0,

      stickers: undefined,

      callToActions: undefined,

      loadedSources: new Set(),

      // TODO: categoryPostfix need only for analytic. Refactor analytic mixin
      categoryPostfix: "SR",
    };
  },

  created() {
    if (this.storyMedia.interactions) {
      this.interactions = this.storyMedia.interactions;
    }

    if (this.storyMedia.stickers) {
      this.stickers = this.storyMedia.stickers;
    }

    if (this.storyMedia.callToActions) {
      this.callToActions = this.storyMedia.callToActions;
    }
  },

  computed: {
    slide() {
      const currentSlide = this.album.posts[this.selectedPostIndex];
      return currentSlide;
    },

    nextSlide() {
      const nextSlideAfterCurrent =
        this.album.posts[this.selectedPostIndex + 1];
      return nextSlideAfterCurrent;
    },

    highlightImagePositionStyles() {
      if (!this.storyMedia || !this.storyMedia.view) {
        return {};
      }

      const { story } = this.storyMedia.view;

      if (!story) {
        return {};
      }

      const styles = getImagePositionStyles(story);
      return styles;
    },

    backgroundImageStyles() {
      if (this.imageSource?.imageHighRes?.length > 0) {
        const imageUrl = this.previewImage;
        //NOTE: Task: create new post from zero.
        // If `const imageUrl = this.previewImage;` from prev line
        // doesn't work try this:
        // const imageUrl = this.previewImage && this.imageSource.imageHighRes;

        return {
          "background-image": this.storyMedia.videoUrl
            ? "none"
            : imageUrl && !this.storyMedia.videoUrl && `url(${imageUrl})`,
        };
      } else if (this.storyMedia.background?.length > 0) {
        return {
          background: this.storyMedia.background,
        };
      } else {
        return new Error("preview image or background doesn't exist");
      }
    },

    storyMedia() {
      if (!this.slide) return {};

      return this.slide.media[0] || {};
    },

    imageSource() {
      const { storyMedia } = this;

      return this.imageURL(storyMedia, true);
    },
  },

  watch: {
    slide: {
      handler() {
        this.clickedEmojiIconUrl = undefined;
        this.isEmojiQuerySuccess = false;
        this.isPollQuerySuccess = false;
        this.isQuestionQuerySuccess = false;
        this.isSelectedInputInteraction = false;

        this.interactions = this.storyMedia.interactions;
        this.isShowEmojiAnimation = false;
        this.stickers = this.storyMedia.stickers;
        this.callToActions = this.storyMedia.callToActions;

        if (this.loadFailTimeout) {
          clearTimeout(this.loadFailTimeout);
        }

        if (this.isSelectedAlbum) {
          if (
            (!this.loadedSources.has(this.imageSource.imageHighRes) &&
              !this.loadedSources.has(
                this.backgroundImageStyles["background-image"]
              )) ||
            this.storyMedia.videoUrl
          ) {
            this.contentIsLoaded = false;
          } else {
            this.$emit("resume-slide");
          }

          this.preloadNext();

          // this.loadFailTimeout = setTimeout(() => {
          //   if (!this.contentIsLoaded) {
          //     // this.$emit("change-post");
          //   }
          // }, 15000);
        }
      },
      deep: false,
      immediate: true,
    },

    isSelectedAlbum: {
      handler(value) {
        if (value && !this.contentIsLoaded) {
          this.load();
        }
      },
      immediate: true,
    },

    contentIsLoaded: {
      handler(isLoaded) {
        if (isLoaded) {
          clearTimeout(this.loadFailTimeout);

          if (this.isSelectedAlbum) {
            this.$emit("resume-slide");
          }
        }
      },
    },

    countOfActiveQueries(count) {
      this.$emit("getCountOfActiveQueries", count);
    },
  },

  methods: {
    selectAlbum() {
      if (!this.isSelectedAlbum) {
        this.$emit("change-album");
      }
    },

    getKeyByParams(params) {
      if (typeof params !== "object") return params;

      return Object.values(params).join("_");
    },

    sendClickLinkAnalytic(link, linkTitle) {
      const story = this.album.posts[this.selectedPostIndex];
      this.sendAnalyticEvent("ClickLink", {
        provPostId:
          story.instagramPost?.frisbuyId ?? story.providerPost.frisbuyId,
        fbProduct: story.frisbuyPost.id,
        igCode: story.providerPost
          ? story.providerPost.shortcode
          : story.instagramPost.shortcode,
        // link: good.id,
        linklabel: linkTitle,
        // offer: good.sku.offerId,
        linkUrl: link,
      });
    },

    async onClickInteractionHandler(interaction) {
      const savedPostId = Number(this.selectedPostIndex);
      const savedAlbumId = Number(this.album.id);

      const CODE = getAnonimousUserCode();
      const API_BODY = {
        code: CODE,
        loggedInId: this.settings.logged_in_id || null,
        email: this.settings.logged_in_email || null,
        phone: this.settings.logged_in_phone || null,
        username: this.settings.logged_in_name || null,
        embedId: this.settings.embedId,
        frisbuyPostId: this.slide.frisbuyPost.id,
        carouselPosition: null,

        questionAnswer:
          interaction.type === "question" ? interaction.answer : null,
        emojiId: interaction.type === "emoji" ? interaction.id : null,
        pollVote: interaction.type === "poll" ? interaction.answer.id : null,
      };

      this.countOfActiveQueries += 1;
      sendInteraction(API_BODY).then((isSuccess) => {
        this.countOfActiveQueries -= 1;

        if (!isSuccess) {
          return;
        }

        if (interaction.type === "question") {
          if (savedPostId != this.selectedPostIndex || savedAlbumId != this.album.id) return;

          this.isQuestionQuerySuccess = true;
        }

        if (interaction.type === "emoji") {
          this.interactions[interaction.index].emojis[
            interaction.emojiIndex
          ].isSelected = true;

          if (savedPostId != this.selectedPostIndex || savedAlbumId != this.album.id) return;

          this.isEmojiQuerySuccess = true;
        }

        if (interaction.type === "poll") {
          const updatedPolls = this.interactions[interaction.index].polls.map(
            (poll) => {
              let pollCopy = JSON.parse(JSON.stringify(poll));

              if (poll.id === interaction.answer.id) {
                pollCopy.isSelected = true;
                pollCopy.count += 1;
              }

              return pollCopy;
            }
          );

          this.interactions[interaction.index].polls = updatedPolls;

          if (savedPostId != this.selectedPostIndex || savedAlbumId != this.album.id) return;

          this.isPollQuerySuccess = true;
        }
      });

      if (interaction.type === "emoji") {
        this.showEmojiAnimation(interaction.url);
      }
    },

    questionFormSelectInputHandler(isSelected) {
      this.isQuestionQuerySuccess = false;
      this.isSelectedInputInteraction = isSelected;

      if (isSelected) {
        this.$emit("pauseSlide");
      } else {
        this.$emit("playSlide");
      }
    },

    showEmojiAnimation(emojiIcon) {
      this.clickedEmojiIconUrl = emojiIcon;

      this.$nextTick(() => {
        this.isShowEmojiAnimation = true;
      });
    },

    getInteractionPositionStyles(coords) {
      const styles = getImagePositionStyles(coords);
      return styles;
    },

    getDomainSubstitute(srcUrl) {
      return DomainSubstitute(srcUrl);
    },

    errorImageHandler(e) {
      if (this.storyMedia.videoUrl) return;

      if (this.backgroundImageStyles["background"]) {
        this.contentIsLoaded = true;

        return;
      }

      MediaCatcher(e, 2, widgetTypes.HIGHLIGHTS, () => {
        setTimeout(() => {
          if (this.isSelectedAlbum) {
            this.$emit("navigationSwitchToNext", 1);
          }
        }, 1000);
      });
      this.load();
    },

    errorVideoHandler(e) {
      MediaCatcher(e, 2, widgetTypes.HIGHLIGHTS, () => {
        setTimeout(() => {
          if (this.isSelectedAlbum) {
            this.$emit("navigationSwitchToNext", 1);
          }
        }, 1000);

        return;
      });
    },

    load() {
      const { image, video } = this.$refs;

      this.previewImage = undefined;

      if (!this.slide || !image) return;

      if (!image.dataset.src) {
        if (this.storyMedia.background?.length > 0) {
          this.contentIsLoaded = true;

          this.loadedSources.add(this.storyMedia.background);
        }

        return;
      }

      image.src = image.dataset.src;

      if (this.contentIsLoaded) {
        this.previewImage = image.src;

        return;
      }

      image.onload = () => {
        this.previewImage = image.src;
        this.loadedSources.add(image.src);

        if (!video) {
          this.contentIsLoaded = true;
        }
      };

      if (video) {
        if (
          video.src &&
          video.src === video.dataset.src &&
          this.contentIsLoaded
        ) {
          console.warn(
            "Prevented attempt to load a video that has already been loaded"
          );
          return;
        }

        video.src = video.dataset.src;

        /*
          NOTE: Normal browsers load video on set video.src =...
          But Safari need video.load() func
        */
        video.load();

        return;
      }
    },

    play(setTransition) {
      const { preview, contentIsLoaded, isSelectedAlbum } = this;
      const { video } = this.$refs;

      if (!contentIsLoaded || !isSelectedAlbum) return;

      if (video) {
        const playPromise = video.play();

        if (setTransition) {
          playPromise.then(() => {
            setTimeout(() => this.preview.checkAudio(video), 300);
            setTransition(video.duration);
          });
        }

        playPromise.catch((error) => {
          if (error.code === 9) {
            preview.switchToNext();
          } else {
            console.error(error);
            video.muted = true;
            // TODO: Check this handler. It's works?
            this.play();
          }
        });
      } else if (setTransition) {
        setTransition(5);
      }
    },

    stop() {
      const { video } = this.$refs;

      if (!video) return;

      video.pause();
      video.currentTime = 0;
    },

    pause() {
      const { video } = this.$refs;

      if (!video) return;

      video.pause();
    },

    preloadNext() {
      const { nextSlide } = this;

      if (nextSlide) {
        const imgToPreload = new Image();

        const nextSlideImageSource = this.imageURL(nextSlide.media[0], true);

        imgToPreload.src = DomainSubstitute(nextSlideImageSource.imageHighRes);
      }
    },
  },
};
</script>

<style lang="scss">
@keyframes slide-switch {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.frisbuy-blur-background {
  filter: blur(10px);
}

.slide-switch-enter-active {
  animation: slide-switch 0.5s;
  z-index: 2;
}

.frisbuy-slide {
  transform: scale(0.4533);
  vertical-align: baseline;
  flex-shrink: 0;
  transition: transform 0.25s ease-out, margin 0.25s ease-out;
  will-change: transform, margin;
  visibility: visible;
  cursor: pointer;
  // margin-left: -8vw;
  // margin-right: -8vw;
  background: transparent;
  opacity: 0.55;
  height: var(--frisbuy-story-slide-height);
  width: var(--frisbuy-story-slide-width);
  aspect-ratio: 9 / 16;
  position: relative;

  @media screen and (max-width: 767px) {
    // Fix for mobile positioning
    // if bugged comments this 3 lines
    // and uncomment `height: 100%`
    // display: flex;
    // flex-direction: column;
    // justify-content: center;
    height: 100%;

    scale: initial !important;

    aspect-ratio: 9 / 16;
    max-width: 100dvw;
    max-height: 100dvh;
    // height: 100%;
    // background-color: rgba(0, 0, 0, 0.5);
    margin: 0;
  }

  &--current {
    transform: initial;
    // margin-left: 2vw;
    // margin-right: 2vw;
    opacity: 1;

    // @media (max-width: 767px) {
    //   margin-left: 50%;
    //   margin-right: 50%;
    //   transform: translateX(-50%);
    // }
  }

  .control-elements {
    display: flex;
    position: absolute;
    top: 16px;
    right: 19px;

    flex-direction: row;

    z-index: 3;

    & > *:not(:first-child) {
      margin-left: 26px;
    }
  }
}

.frisbuy-slide {
  .frisbuy-story-slide {
    /*
      Only with this style
      image in slide has width and height
      if set coords for positioning
    */
    // position: relative !important;
    background-size: cover;

    img {
      display: block;
      position: relative;
      width: 100%;
      height: auto;

      // Doesn't work in Safari [15.6, 16.5, 17+]
      // max-width: 100%;
      // max-height: 100%;
    }

    img {
      visibility: hidden;
    }

    video {
      width: 100%;
      height: 100%;
      max-width: 100dvw;
      max-height: 100dvh;
    }
  }

  &--current {
    overflow: hidden;
  }
}

.frisbuy-story-slide-running {
  z-index: 10;
}

.frisbuy-slide-background-click-blocker {
  position: absolute;
  inset: 0;
}

.frisbuy-interaction-frame,
.call-to-action {
  z-index: 2;
}

.frisbuy-emoji-animation {
  z-index: 3;
}
</style>
